import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import type {
  IApiGeneralParams,
  IApiPaginatedResponse,
  IApiResponse,
} from "@/types";

import {
  API_COMPANY_ANNOUNCEMENTS,
  API_COMPANY_ANNOUNCEMENTS_TYPE,
  API_COMPANY_BULK_DELETE_ANNOUNCEMENTS,
  API_COMPANY_DELETE_ANNOUNCEMENTS_BY_ID,
  API_COMPANY_ANNOUNCEMENTS_STATUS,
  API_COMPANY_USERS_READED_ANNOUNCEMENTS,
  API_COMPANY_TRIGGER_ANNOUNCEMENT_BY_ID,
  API_COMPANY_UPDATE_ANNOUNCEMENT_STATUS,
  API_COMPANY_GET_ANNOUNCEMENT_BY_ID,
} from "@/constants/apiEndpoints";
import type {
  IAnnouncementReadedUsers,
  IAnnouncementType,
  ICompanyAnnouncement,
  ICompanyAnnouncementStats,
} from "@/types/(company)/announcement.interface";

export const companyAnnouncementApi = createApi({
  reducerPath: "companyAnnouncementApi",
  baseQuery: dynamicBaseQuery,
  refetchOnFocus: false,
  tagTypes: ["companyAnnouncements"],
  endpoints: (builder) => ({
    getCompanyAnnouncements: builder.query<
      IApiResponse<ICompanyAnnouncement[]>,
      IApiGeneralParams & { type?: string; subject?: string; isRead?: boolean; platformSectionType?: string; sortBy?: string; sortOrder?: string }
    >({
      query: (params) => ({
        url: API_COMPANY_ANNOUNCEMENTS,
        method: "GET",
        params,
      }),
      providesTags: ["companyAnnouncements"],
    }),
    createCompanyAnnouncement: builder.mutation<
      IApiPaginatedResponse<void>,
      { payload: FormData }
    >({
      query: ({ payload }) => {
        return {
          url: API_COMPANY_ANNOUNCEMENTS,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ["companyAnnouncements"],
    }),
    updateCompanyAnnouncement: builder.mutation<
      IApiPaginatedResponse<void>,
      { id: number; payload: FormData }
    >({
      query: ({ id, payload }) => ({
        url: `/company/announcements/${id}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["companyAnnouncements"],
    }),
    getCompanyAnnouncementTypes: builder.query<
      IApiResponse<IAnnouncementType[]>,
      void
    >({
      query: () => ({
        url: API_COMPANY_ANNOUNCEMENTS_TYPE,
        method: "GET",
      }),
    }),
    getCompanyAnnouncementStats: builder.query<
      IApiPaginatedResponse<ICompanyAnnouncementStats>,
      void
    >({
      query: () => ({
        url: API_COMPANY_ANNOUNCEMENTS_STATUS,
        method: "GET",
      }),
    }),
    getAnnouncementReadedUsers: builder.query<
      IApiPaginatedResponse<IAnnouncementReadedUsers[]>,
      IApiGeneralParams & { announcementId: number }
    >({
      query: ({ announcementId, ...params }) => ({
        url: API_COMPANY_USERS_READED_ANNOUNCEMENTS(announcementId),
        method: "GET",
        params,
      }),
    }),
    deleteCompanyAnnouncementById: builder.mutation<
      IApiPaginatedResponse<void>,
      number
    >({
      query: (announcementId) => ({
        url: API_COMPANY_DELETE_ANNOUNCEMENTS_BY_ID(announcementId),
        method: "DELETE",
      }),
      invalidatesTags: ["companyAnnouncements"],
    }),
    deleteCompanyAnnouncementsBulk: builder.mutation<
      IApiPaginatedResponse<void>,
      { ids: number[] }
    >({
      query: (body) => ({
        url: API_COMPANY_BULK_DELETE_ANNOUNCEMENTS,
        method: "POST",
        body,
      }),
      invalidatesTags: ["companyAnnouncements"],
    }),
    triggerCompanyAnnouncementById: builder.mutation<
      IApiPaginatedResponse<void>,
      number
    >({
      query: (announcementId) => ({
        url: API_COMPANY_TRIGGER_ANNOUNCEMENT_BY_ID(announcementId),
        method: "PATCH",
      }),
      invalidatesTags: ["companyAnnouncements"],
    }),
    updateCompanyAnnouncementStatus: builder.mutation<
      IApiPaginatedResponse<void>,
      number
    >({
      query: (announcementId) => ({
        url: API_COMPANY_UPDATE_ANNOUNCEMENT_STATUS(announcementId),
        method: "PATCH",
      }),
      invalidatesTags: ["companyAnnouncements"],
    }),
    getCompanyAnnouncementById: builder.query<
      IApiResponse<ICompanyAnnouncement>,
      number
    >({
      query: (announcementId) => ({
        url: API_COMPANY_GET_ANNOUNCEMENT_BY_ID(announcementId),
        method: "GET",
      }),
    }),
  }),
});

export const {
  useCreateCompanyAnnouncementMutation,
  useUpdateCompanyAnnouncementMutation,
  useDeleteCompanyAnnouncementByIdMutation,
  useDeleteCompanyAnnouncementsBulkMutation,
  useGetCompanyAnnouncementsQuery,
  useGetAnnouncementReadedUsersQuery,
  useGetCompanyAnnouncementStatsQuery,
  useGetCompanyAnnouncementTypesQuery,
  useUpdateCompanyAnnouncementStatusMutation,
  useTriggerCompanyAnnouncementByIdMutation,
  useGetCompanyAnnouncementByIdQuery,
} = companyAnnouncementApi;
